import { Component, OnInit, Output, EventEmitter, Inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { AuthenticationService } from '../../core/services/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from '../../core/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'src/app/core/services/notification.service';
import { SellService } from 'src/app/core/services/sell.service';
import Swal from 'sweetalert2';
import { CommonService } from 'src/app/core/services/common.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit, OnDestroy {

  element: any;
  cookieValue: string;
  flagvalue: string;
  countryName: string;
  openMobileMenu: boolean;
  _userName: string = '';
  valueset: string = 'assets/images/flags/us.jpg';

  notifications: any[] = [];
  hasSeenNotifications: boolean = false;

  pendingInvoices: any[] = [];
  pendingInvoiceCount: number = 0;
  lastPendingCount: number = 0;

  /** ⭐ NEW – Phase detection + Safety */
  isPhase2Enabled: boolean = false;
  isPhaseChecked: boolean = false;   // <– IMPORTANT
  topbarPhaseLabel: string = '';
  topbarPhaseNumber: number | null = null;

  pendingInterval: any = null;

  listLang = [
    { text: 'English', flag: 'assets/images/flags/us.jpg', lang: 'en' },
    { text: 'عربى', flag: 'assets/images/flags/KSA.jpg', lang: 'ar' }
  ];

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    private authService: AuthenticationService,
    public languageService: LanguageService,
    public translate: TranslateService,
    public _cookiesService: CookieService,
    private notificationService: NotificationService,
    private sellService: SellService,
    private CommonService: CommonService
  ) {}

  ngOnInit() {

    this.openMobileMenu = false;
    this.element = document.documentElement;

    /** Load storecode */
    let storecode = '';
    try {
      const rawUser = sessionStorage.getItem(environment.dataKey);
      const decoded = JSON.parse(this.CommonService.atou(rawUser!));
      const registerDetail = JSON.parse(decoded[0].registerdetail);
      storecode = registerDetail[0].storecode;
      sessionStorage.setItem("storecode", storecode);
    } catch {}

    /** Language */
    this.cookieValue = sessionStorage.getItem('lang');
    const selectedLang = this.listLang.find(x => x.lang === this.cookieValue);
    this.countryName = selectedLang ? selectedLang.text : 'English';
    this.flagvalue = selectedLang ? selectedLang.flag : 'assets/images/flags/us.jpg';

    if (selectedLang) {
      this.languageService.setLanguage(selectedLang.lang);
      this.languageService.LanguageChange.next(selectedLang.lang);
    }

    /** User */
    if (this.authService.currentUser() != null) {
      const userData = this.authService.currentUser();
      this._userName = userData[0]["username"];
    }

    this.hasSeenNotifications = sessionStorage.getItem('notificationsSeen') === 'true';

    /** Always load general notifications */
    this.loadNotifications();

    /** Load store phase → Controls pending invoice logic */
    this.checkStorePhase(storecode);
  }

  ngOnDestroy(): void {
    if (this.pendingInterval) {
      clearInterval(this.pendingInterval);
    }
  }

  /** ⭐ GET STORE PHASE */
  checkStorePhase(storecode: string) {
    if (!storecode) {
      this.topbarPhaseLabel = 'PHASE UNKNOWN';
      this.topbarPhaseNumber = null;
      return;
    }

    this.authService.getStoreConfig(storecode).subscribe({
      next: (res: any) => {

        this.isPhase2Enabled = (String(res?.phase) === '2');
        // Provide a simple, display-friendly phase label for the topbar
        const phaseNum = Number(res?.phase) || null;
        this.topbarPhaseNumber = phaseNum;
        this.topbarPhaseLabel = phaseNum ? `PHASE ${phaseNum}` : 'PHASE UNKNOWN';
        this.isPhaseChecked = true;  // <– MARK PHASE LOADED

        /** Clean previous interval (if any) */
        if (this.pendingInterval) clearInterval(this.pendingInterval);

        if (this.isPhase2Enabled) {
          /** ⭐ Phase 2 – Enable logic */
          this.loadPendingInvoices();

          this.pendingInterval = setInterval(() => {
            this.loadPendingInvoices(true);
          }, 120000); 

        } else {
          this.pendingInvoices = [];
          this.pendingInvoiceCount = 0;
          this.lastPendingCount = 0;
          sessionStorage.setItem("lastPendingCount", "0");
        }
      },
      error: () => {
        this.isPhaseChecked = true;
        this.isPhase2Enabled = false;
        this.topbarPhaseLabel = 'PHASE UNKNOWN';
        this.topbarPhaseNumber = null;
      }
    });
  }

  /** 🔔 Notifications (always load) */
  loadNotifications() {
    this.notificationService.getAllNotifications().subscribe({
      next: (res: any) => {
        const now = new Date();
        this.notifications = res.filter((n: any) => {
          const start = new Date(n.startdate);
          const end = new Date(n.enddate);
          return start <= now && now <= end;
        });
      }
    });
  }

  /** ⭐ PENDING INVOICE NOTIFICATION (Phase 2 only + Safe guards) */
  loadPendingInvoices(isAutoRefresh = false) {

    /** 🚫 DO NOT RUN BEFORE PHASE IS KNOWN */
    if (this.isPhaseChecked === false) return;

    /** 🚫 DO NOT RUN IN PHASE 1 */
    if (this.isPhase2Enabled !== true) return;

    const storecode = sessionStorage.getItem('storecode');
    if (!storecode) return;

    this.sellService.getPendingInvoices(storecode).subscribe({
      next: (list: any[]) => {

        this.pendingInvoices = list || [];
        this.pendingInvoiceCount = this.pendingInvoices.length;

        /** Only auto-popup for new items */
        if (isAutoRefresh && this.pendingInvoiceCount > this.lastPendingCount) {
          const newItems = this.pendingInvoiceCount - this.lastPendingCount;
          Swal.fire({
            icon: 'warning',
            title: `${newItems} NEW Pending Invoices`,
            text: 'Some invoices need to be pushed to ZATCA.',
            timer: 3000
          });
        }

        this.lastPendingCount = this.pendingInvoiceCount;
        sessionStorage.setItem("lastPendingCount", String(this.lastPendingCount));
      }
    });
  }

  goToPendingInvoices() {
    this.router.navigate(['/epage/sell/pendinginvoicestopush']);
  }

  onDropdownToggle(isOpen: boolean) {
    if (isOpen && !this.hasSeenNotifications) {
      this.hasSeenNotifications = true;
    }
  }

  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;

    this.languageService.setLanguage(lang);
    this.languageService.LanguageChange.next(lang);
  }

  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  logout() {
    sessionStorage.clear();
    this.authService.logout();
    this.router.navigate(['login']);
  }

  fullscreen() {
    document.body.classList.toggle('fullscreen-enable');
    if (!document.fullscreenElement) this.element.requestFullscreen?.();
    else this.document.exitFullscreen?.();
  }
}
